<template>
  <modal
    size="full"
    :title="title"
    :open="open"
    @close="closeModal"
  >
    <div>
      <div class="grid">
        <section>
          <image-upload
            v-if="showImageUpload"
            v-model="post.image"
            name="image"
            label="Upload an image for the post"
          />

          <text-input
            id="title"
            v-model.trim="post.title"
            label="Title"
            required
          />

          <text-input
            id="link"
            v-model.trim="post.link"
            type="url"
            label="Link"
          />

          <select-input
            id="category"
            v-model="post.category"
            label="Category"
            :options="postCategories"
            required
          />

          <text-area-input
            id="body"
            v-model="post.body"
            label="Body"
            required
          />
        </section>

        <section>
          <div
            v-for="(cardNote, i) in post.cardNotes"
            :key="i"
            class="card-note"
          >
            <div class="card-search-container">
              <div v-if="post.cardNotes[i].card" class="card-note-label">
                <align flex>
                  <div><b>{{ post.cardNotes[i].cardLabel || post.cardNotes[i].card }}</b></div>
                  <icon-button
                    class="remove"
                    icon="delete_outline"
                    @click="removeCardNote(i)"
                  />
                </align>
              </div>

              <card-search
                v-else
                placeholder="Search for a card"
                @select="setCardNote(i, $event)"
              />
            </div>

            <div v-show="post.cardNotes[i].card" flex>
              <text-input
                v-model.trim="post.cardNotes[i].title"
                label="Title"
              />

              <text-area-input
                v-model="post.cardNotes[i].text"
                label="Notes"
              />
            </div>
          </div>

          <add-link @click="addCardNote">
            Add card note
          </add-link>
        </section>
      </div>
    </div>

    <template #footer>
      <btn :loading="saving" @click="submit">
        {{ buttonText }}
      </btn>
    </template>
  </modal>
</template>

<script>
import clone from 'lodash.clonedeep';
import * as Sentry from '@sentry/vue';

import { cardService, saleService, feedService } from '@/services';
import { POST_CATEGORIES } from '@cardladder/shared/constants';
import { getGeneratedAllTimeHighNote } from '@cardladder/shared/utils/feed.util';

import CardSearch from '@/cards/CardSearch';

const DEFAULT = {
  id: null,
  image: null,
  title: '',
  link: '',
  category: 'Headline',
  body: '',
  cardNotes: [],
};

export default {
  components: {
    CardSearch,
  },

  data() {
    return {
      open: false,
      id: null,
      post: clone(DEFAULT),
      postCategories: POST_CATEGORIES,
      saving: false,
    };
  },

  mounted() {
    this.$bus.$on('admin-edit-post', this.handleEdit);
    this.$bus.$on('admin-create-post', this.handleCreate);
  },

  beforeDestroy() {
    this.$bus.$off('admin-edit-post', this.handleEdit);
    this.$bus.$off('admin-create-post', this.handleCreate);
  },

  computed: {
    editing() {
      return Boolean(this.id);
    },

    title() {
      return this.editing ? 'Edit Post' : 'Add Post';
    },

    buttonText() {
      return this.editing ? 'Save' : 'Submit';
    },

    showImageUpload() {
      return this.post.category === 'Update';
    },
  },

  methods: {
    handleEdit(id) {
      this.id = id;

      this.load();
      this.open = true;
    },

    handleCreate(post) {
      this.post = post;
      this.open = true;
    },

    async load() {
      if (this.editing) {
        try {
          this.post = await feedService.getPost(this.id);
        } catch (e) {
          this.$toasted.error('Error loading the post');
          Sentry.captureException(e);
        }
      }
    },

    async submit() {
      this.saving = true;

      try {
        this.post.cardNotes = this.post.cardNotes ? this.post.cardNotes.filter(({
          card, cardLabel, text, title,
        }) => card && cardLabel && text && title) : [];

        await feedService.createOrUpdate(this.post);

        if (!this.post.id) {
          this.reset();
        }

        this.$toasted.success('Post saved!');

        if (!this.editing) {
          this.$bus.$emit('posts-changed');
        }

        this.closeModal();
      } catch (e) {
        this.$toasted.error('Error saving the post');
        Sentry.captureException(e);
      }

      this.saving = false;
    },

    addCardNote() {
      if (!this.post.cardNotes) {
        this.post.cardNotes = [];
      }

      this.post.cardNotes.push({ card: null, text: '', title: '' });
    },

    removeCardNote(i) {
      this.post.cardNotes.splice(i, 1);
    },

    async setCardNote(i, card) {
      this.post.cardNotes[i].card = card.id;
      this.post.cardNotes[i].cardLabel = card.label || '';
      this.post.cardNotes[i].cardImage = card.image || '';
      this.post.cardNotes[i].id = card.id;
      this.post.cardNotes[i].year = card.year || '';
      this.post.cardNotes[i].set = card.set || '';
      this.post.cardNotes[i].player = card.player || '';
      this.post.cardNotes[i].condition = card.condition || '';
      this.post.cardNotes[i].variation = card.variation || '';
      this.post.cardNotes[i].image = card.image || '';
      this.post.cardNotes[i].number = card.number || '';

      if (this.post.category === 'Headline') {
        const cardNote = await getGeneratedAllTimeHighNote(card.id, cardService, saleService, i);

        const updatedNote = {
          ...this.post.cardNotes[i],
          ...cardNote,
        };

        this.$set(this.post.cardNotes, i, updatedNote);
      }
    },

    reset() {
      this.post = clone(DEFAULT);
    },

    closeModal() {
      this.reset();
      this.open = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.card-note-label  {
  margin-bottom: $gp;
}

.add-link {
  margin-top: $gp;

  @media($medium-up) {
    margin-top: 0;
  }
}

.card-note {
  margin-bottom: $gp;
}

form {
  @media($medium-up) {
    max-width: 100%;
  }
}

.grid {
  display: grid;
  grid-gap: $gp;
  grid-template-columns: repeat(2, 1fr);
  margin-bottom: $gp;

  @media($medium-down) {
    display: block;
  }
}
</style>
